import { computed, ref } from 'vue';
import {
  DEFAULT_SCENE7_MODIFIERS,
  ADJUSTED_SCENE7_MODIFIERS,
} from './constants';

/**
 * A composable for rebuilding url based on scene7 parameters
 * @param {object} options
 * @param {import('vue').Ref<Object>} options.pt - pass attributes to DOM elements inside the component
 * @param {import('vue').Ref<Boolean>} options.nativeLazy - Indicate whether to use native lazyloading behavior instead of intersectionObserver
 * @param {import('vue').Ref<Boolean | String>} options.preset - Indicates if the image filepath has preset setting
 * @param {import('vue').Ref<Number | 0.8>} options.aspectRatio - Specified image aspect ratio
 * @param {import('vue').Ref<Object>} options.imageModifiers - Indicating a set of the image command modifiers
 * @param {import('vue').Ref<Boolean>} options.setScene7Hei - Indicate need to set scene7 height based on aspect ratio
 * @param {import('vue').Ref<Boolean>} options.lazyLoad - Indicate whether need lazy loading image
 * @param {import('vue').Ref<String>} options.imageUrl - image url
 * @param {import('vue').Ref<String>} options.imageType - Indicate image type
 * @param {import('vue').Ref<Boolean>} options.usingScene7 - Indicate whether using scene7
 * @param {import('vue').Ref<Boolean>} options.retinaAdjustment - Indicate that retina picture optimization should be applied
 * @param {import('vue').Ref<String>} options.retinaQuality - Indicates the qlt parameter retina displays
 * @param {import('vue').Ref<Object>} options.imageSizesByBreakpoints - Indicating a set of the responsive image sizes
 * @param {import('vue').Ref<Object>} options.imageUrlsByBreakpoints - Indicating a set of the responsive image urls
 * @param {import('vue').Ref<Number | null>} options.imageIntrinsicWidth - define width used by image placeholder or skeleton
 * @param {import('vue').Ref<Boolean>} options.lazyLoaded - Indicates if image is already lazy loaded
 */
export default function useImageSrc({
  // default value should match with props default value
  pt = ref({}),
  nativeLazy = ref(false),
  preset = ref(false),
  aspectRatio = ref(0.8),
  imageModifiers = ref({}),
  setScene7Hei = ref(false),
  lazyLoad = ref(true),
  imageUrl = ref(''),
  imageType: _imageType = ref(''),
  usingScene7 = ref(true),
  retinaAdjustment = ref(false),
  retinaQuality = ref(''),
  imageSizesByBreakpoints = ref({}),
  imageUrlsByBreakpoints = ref({}),
  imageIntrinsicWidth = ref(null),
  lazyLoaded = ref(false),
}) {
  const imgWidth = computed(() => {
    if (imageIntrinsicWidth.value === null) {
      return 240;
    }
    return Math.round(imageIntrinsicWidth.value);
  });

  const retinaQlt = computed(() => (retinaQuality ? retinaQuality.value : DEFAULT_SCENE7_MODIFIERS.qlt));

  const responsive = Object.keys(imageSizesByBreakpoints.value).length
    || Object.keys(imageUrlsByBreakpoints.value).length;

  /**
  * Construct complete scene7 url based on different parameters.
  * @returns {String} scene7 url
  */
  const getScene7ImgFullUrl = ({
    breakpointName,
    imageType,
    density = 1,
    isRetina,
  } = {}) => {
    const queryString = (obj = undefined, sep = '&', eq = '=') => Object.keys(obj)
      // cmd with/without value
      .map((k) => (obj[k] ? k + eq + obj[k] : k))
      .join(sep);
    const url = new URL(imageUrl.value);
    const urlWithoutQueryParams = `${url.href.split('?')[0]}?`;
    const paramsFromUrl = {};

    url.searchParams.forEach((value, key) => {
      if (value === '') {
        paramsFromUrl[key] = null;
      } else {
        paramsFromUrl[key] = value;
      }
    });
    if (responsive) {
      const responsiveRetinaModifiers = {
        qlt: isRetina ? retinaQlt.value : DEFAULT_SCENE7_MODIFIERS.qlt,
        wid: imageSizesByBreakpoints.value[breakpointName] * density,
        fmt: imageType,
      };
      if (retinaAdjustment.value) {
        return setScene7Hei.value
          ? `${urlWithoutQueryParams}${queryString({
            ...ADJUSTED_SCENE7_MODIFIERS,
            ...imageModifiers.value,
            ...responsiveRetinaModifiers,
            hei: Math.round(
              (imageSizesByBreakpoints.value[breakpointName] * density)
              / aspectRatio.value,
            ),
          })}`
          : `${urlWithoutQueryParams}${queryString({
            ...ADJUSTED_SCENE7_MODIFIERS,
            ...imageModifiers.value,
            ...responsiveRetinaModifiers,
          })}`;
      }
      return setScene7Hei.value
        ? `${urlWithoutQueryParams}${queryString({
          ...DEFAULT_SCENE7_MODIFIERS,
          ...paramsFromUrl,
          ...imageModifiers.value,
          wid: imageSizesByBreakpoints.value[breakpointName] * density,
          hei: Math.round(
            (imageSizesByBreakpoints.value[breakpointName] * density)
            / aspectRatio.value,
          ),
          fmt: imageType,
        })}`
        : `${urlWithoutQueryParams}${queryString({
          ...DEFAULT_SCENE7_MODIFIERS,
          ...paramsFromUrl,
          ...imageModifiers.value,
          wid: imageSizesByBreakpoints.value[breakpointName] * density,
          fmt: imageType,
        })}`;
    }
    let queryParams = {
      ...paramsFromUrl,
      ...imageModifiers.value,
      fmt: imageType,
    };
    if (retinaAdjustment.value) {
      if (isRetina) {
        queryParams = {
          ...imageModifiers.value,
          fmt: 'webp',
          qlt: retinaQlt.value,
          wid: imgWidth.value * density,
        };
      } else {
        queryParams.qlt = ADJUSTED_SCENE7_MODIFIERS.qlt;
      }
    }
    if (setScene7Hei.value) {
      queryParams = 'wid' in queryParams
        ? {
          ...queryParams,
          hei: Math.round(queryParams.wid / aspectRatio.value),
        }
        : {
          ...queryParams,
          wid: imgWidth.value,
          hei: Math.round(imgWidth.value / aspectRatio.value),
        };
    } else {
      queryParams = 'wid' in queryParams
        ? queryParams
        : { ...queryParams, wid: imgWidth.value };
    }
    if (preset.value) {
      delete queryParams.wid;
      delete queryParams.hei;
    }
    return `${urlWithoutQueryParams}${queryString(queryParams)}`;
  };

  /**
  * Construct either scene7 url or provided image urls based on breakpoints
  * @returns {String} image url
  */
  const getImgFullUrl = ({
    breakpointName = 'medium',
    imageType = _imageType.value || 'jpeg',
    density = 1,
    isRetina = false,
  } = {}) => {
    if (usingScene7.value) {
      return getScene7ImgFullUrl({
        breakpointName,
        imageType,
        density,
        isRetina,
      });
    }
    const url = breakpointName in imageUrlsByBreakpoints.value
      ? imageUrlsByBreakpoints.value[breakpointName]
      : imageUrl.value;
    return url;
  };

  /**
  * @returns {String} image url for <source> srcset
  */
  const getSrcSet = ({ breakpointName, imageType, isRetina } = {}) => {
    if (!lazyLoaded.value && !nativeLazy.value) {
      return null;
    }
    if (retinaAdjustment.value && !responsive) {
      return getImgFullUrl({ density: 2, imageType, isRetina });
    }
    if (usingScene7.value && responsive) {
      return `${getImgFullUrl({ breakpointName, imageType })},
            ${getImgFullUrl({
        breakpointName,
        density: 2,
        imageType,
        isRetina,
      })} 2x`;
    }
    return getImgFullUrl({ breakpointName, imageType });
  };

  /**
  * @returns {String} image url for <img> src
  */
  const imgSrc = () => {
    if (typeof window === 'undefined') {
      // ssr and not lazyload should get full image url
      // pt value take precedence over deprecated prop lazyLoad
      if (pt.value?.img?.loading) {
        return pt.value?.img?.loading === 'lazy' && !nativeLazy.value ? null : getImgFullUrl({ imageType: 'jpeg' });
      }
      return lazyLoad.value && !nativeLazy.value ? null : getImgFullUrl({ imageType: 'jpeg' });
    }
    if (!nativeLazy.value && !lazyLoaded.value) {
      // Use a transparent background instead of passing null for client side because
      // in safari empty src will consider as error and will trigger @error=onImageUrlError
      return `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='${
        imgWidth.value
      }px' height='${Math.round(imgWidth.value / aspectRatio.value)}px' />`;
    }
    return getImgFullUrl({ imageType: 'jpeg' });
  };

  return {
    imgSrc,
    getImgFullUrl,
    getSrcSet,
    responsive,
  };
}
